import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import enCommon from "./locales/en/common.json";
import koCommon from "./locales/ko/common.json";

// The languages the UI ships translations for. English is the source/fallback;
// Korean is translated incrementally (missing keys fall back to English).
export const SUPPORTED_LANGUAGES = ["en", "ko"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// App-wide i18n. Detection precedence: a saved preference (localStorage
// "language") → the browser locale → the <html lang> attribute; English is the
// fallback for anything unmatched or untranslated. We register on the DEFAULT
// react-i18next instance (initReactI18next) so `useTranslation()` works
// everywhere — including the shared front/components rendered by the SPA —
// without threading an explicit <I18nextProvider>. Mirrors how the theme setting
// is persisted to localStorage (see ThemeContext + index.html bootstrap).
void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: enCommon },
      ko: { common: koCommon },
    },
    fallbackLng: "en",
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // Treat region variants as their base language (e.g. ko-KR → ko).
    nonExplicitSupportedLngs: true,
    defaultNS: "common",
    ns: ["common"],
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: "language",
      caches: ["localStorage"],
    },
    // React already escapes interpolated values.
    interpolation: { escapeValue: false },
    returnNull: false,
  });

// Keep <html lang> in sync with the active language (fires on init and on
// every change) so assistive tech and browser translation see the real locale;
// the static entry-HTML attribute is only the pre-init fallback.
i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
});

export default i18n;
